/**
 * @lodex-pro/web-admin/features/operators/dedupe-hooks — "Review duplicates" and the merge dialog
 * (lx-coord/operator-dedupe-plan.md, Corbin 2026-10-20).
 *
 *   GET  /v1/platform/operators/duplicates          the suspected duplicate groups
 *   POST /v1/platform/operators/:id/merge/preview   the merge's exact effect, rolled back
 *   POST /v1/platform/operators/:id/merge           the merge, with the final display name
 *   POST /v1/platform/operators/duplicates/dismissals      "not a duplicate" (0247), and DELETE …/:id to undo
 */
import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery, useApiMutation, useApiOpts } from '@lodex-pro/web-shared';
import { OPERATORS_KEY, OPERATOR_KEY, type MergeResult } from './hooks';

// ─── Types ───────────────────────────────────────────────────────────────────

export type VerdictKind =
  | 'MERGE'
  | 'MERGE_OTHER_STATE'
  | 'MERGE_EMPTY'
  | 'SAME_BRAND'
  /** Shares only the first word: shown for context, never suggested. */
  | 'SAME_FIRST_WORD'
  | 'BOTH_IN_USE'
  /** OPEN-168: in the group only through a shared phone or address (a different name). */
  | 'SAME_CONTACT';

export interface DuplicateVerdict {
  readonly kind: VerdictKind;
  readonly suggestMerge: boolean;
  readonly text: string;
  /** Information only (different regulator numbers, a display name of another company). */
  readonly notes: readonly string[];
}

export interface DuplicateMember {
  readonly id: string;
  readonly name: string;
  readonly displayName: string | null;
  readonly abbreviation: string | null;
  readonly regulator: string | null;
  readonly regulatorOperatorId: string | null;
  readonly state: string | null;
  readonly states: readonly string[];
  readonly isActive: boolean;
  readonly wellCount: number;
  readonly haulerCount: number;
  readonly portalCompanyCount: number;
  /** The haulers using it, by name. */
  readonly haulers: readonly string[];
  /** OPEN-168: the RRC P-5 mailing address and phones; null when unknown (OCC, hand-made). */
  readonly contact?: { readonly address: string | null; readonly phones: readonly string[] } | null;
  readonly isSuggestedSurvivor: boolean;
  /** Its usage in words ("used by 1 hauler customer record · has DVN · 4,212 active wells"). */
  readonly reason: string;
  /** Null for the suggested survivor. */
  readonly verdict: DuplicateVerdict | null;
}

export interface DuplicateGroup {
  readonly key: string;
  /** Null when both leading rows are in use: the dialog makes him choose. */
  readonly survivorId: string | null;
  readonly survivorReason: string;
  readonly members: readonly DuplicateMember[];
  /** First-word-only rows left out of the card because there were too many (else 0). */
  readonly omittedSameFirstWord: number;
}

export interface DisplayNameCollision {
  readonly shownCore: string;
  readonly rows: readonly { readonly id: string; readonly name: string; readonly shown: string }[];
  readonly text: string;
}

/** A live "not a duplicate" ruling (0247), for the page's Undo section. */
export interface DuplicateDismissal {
  readonly id: string;
  readonly operatorA: { readonly id: string; readonly name: string; readonly state: string | null };
  readonly operatorB: { readonly id: string; readonly name: string; readonly state: string | null };
  readonly reason: string | null;
  readonly dismissedAt: string;
}

export interface DuplicatesResponse {
  readonly groups: readonly DuplicateGroup[];
  readonly displayNameCollisions: readonly DisplayNameCollision[];
  readonly dismissals: readonly DuplicateDismissal[];
}

export interface MergeSide {
  readonly id: string;
  readonly name: string;
  readonly displayName: string | null;
  readonly abbreviation: string | null;
  readonly regulator: string | null;
  readonly regulatorOperatorId: string | null;
  readonly state: string | null;
  readonly states: readonly string[];
  readonly isActive: boolean;
  readonly wellCount: number;
  readonly haulerCount: number;
  readonly portalCompanyCount: number;
  readonly reason: string;
}

export interface MergePreview {
  readonly keep: MergeSide;
  readonly mergeAway: MergeSide;
  readonly counts: {
    readonly wellsMoved: number;
    readonly haulersMoved: number;
    readonly haulersFolded: number;
    readonly portalCompaniesRepointed: number;
    readonly jobsRepointed: number;
    readonly locationsRepointed: number;
    readonly siteContactsRepointed: number;
    readonly billableEventsRepointed: number;
    readonly invoicesRepointed: number;
    readonly summariesRepointed: number;
    readonly summariesDeduped: number;
    readonly disposalExclusionsRepointed: number;
    readonly disposalFeesRepointed: number;
    readonly disposalFeesDropped: number;
  };
  readonly droppedFees: readonly {
    readonly id: string;
    readonly feeCentsPerBbl?: number;
    readonly effectiveFrom?: string;
    readonly notes?: string | null;
  }[];
  readonly fields: {
    readonly abbreviation: { readonly kept: string | null; readonly lost: string | null };
    readonly displayName: {
      readonly keep: string | null;
      readonly mergeAway: string | null;
      readonly carried: boolean;
      readonly proposed: string;
    };
    readonly state: { readonly kept: string | null; readonly statesAfter: readonly string[] };
  };
  /** Each one blocks Confirm until "I understand" is ticked. */
  readonly warnings: readonly { readonly code: string; readonly text: string }[];
  readonly caveats: readonly string[];
  readonly haulersNotified: readonly string[];
}

export interface MergeBody {
  sourceOperatorId: string;
  displayName?: string;
  /** The preview's warning codes he ticked "I understand" for; the server re-checks them. */
  acknowledgedWarnings?: readonly string[];
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

export const DUPLICATES_KEY = ['platform', 'operators', 'duplicates'] as const;

export function useOperatorDuplicates(
  enabled = true,
): ReturnType<typeof useApiQuery<DuplicatesResponse>> {
  const opts = useApiOpts();
  return useApiQuery<DuplicatesResponse>(opts, '/v1/platform/operators/duplicates', {
    queryKey: DUPLICATES_KEY,
    enabled,
    staleTime: 30_000,
  });
}

/** The preview for keeping `keepId`. A mutation (it is a POST), re-run on every Swap. */
export function useMergePreview(
  keepId: string,
): ReturnType<typeof useApiMutation<{ sourceOperatorId: string }, MergePreview>> {
  const opts = useApiOpts();
  return useApiMutation<{ sourceOperatorId: string }, MergePreview>(
    opts,
    `/v1/platform/operators/${keepId}/merge/preview`,
    'POST',
  );
}

/** Merge into `keepId`; refreshes the list, both rows, and the duplicate groups. */
export function useMergeInto(
  keepId: string,
): ReturnType<typeof useApiMutation<MergeBody, MergeResult & { displayName: string | null }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<MergeBody, MergeResult & { displayName: string | null }>(
    opts,
    `/v1/platform/operators/${keepId}/merge`,
    'POST',
    {
      onSuccess: (_r, body) => {
        void qc.invalidateQueries({ queryKey: OPERATORS_KEY });
        void qc.invalidateQueries({ queryKey: OPERATOR_KEY(keepId) });
        void qc.invalidateQueries({ queryKey: OPERATOR_KEY(body.sourceOperatorId) });
        void qc.invalidateQueries({ queryKey: DUPLICATES_KEY });
      },
    },
  );
}

export interface DismissBody {
  operatorIds: readonly [string, string];
  reason?: string;
}

/** Rule a pair "not a duplicate" (0247); refreshes the groups and the list chip. */
export function useDismissDuplicate(): ReturnType<
  typeof useApiMutation<DismissBody, { dismissal: { id: string } }>
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<DismissBody, { dismissal: { id: string } }>(
    opts,
    '/v1/platform/operators/duplicates/dismissals',
    'POST',
    { onSuccess: () => void qc.invalidateQueries({ queryKey: OPERATORS_KEY }) },
  );
}

/** Undo a "not a duplicate" ruling. */
export function useUndoDismissal(
  dismissalId: string,
): ReturnType<typeof useApiMutation<Record<string, never>, { undone: boolean }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<Record<string, never>, { undone: boolean }>(
    opts,
    `/v1/platform/operators/duplicates/dismissals/${dismissalId}`,
    'DELETE',
    { onSuccess: () => void qc.invalidateQueries({ queryKey: OPERATORS_KEY }) },
  );
}
